/* ============================================================
   Codeage — Product page (PDP)
   Zero-dependency web components. The Liquid markup works
   without JavaScript; these elements hydrate it.
   ============================================================ */
(() => {
  "use strict";

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* ------------------------------------------------------------
     Gallery: scroll-snap slider + thumbnails + zoom dialog.
     Native horizontal scrolling keeps working when JS is off;
     this element adds buttons, state syncing and the zoom modal.
     ------------------------------------------------------------ */
  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-gallery-track]");
      this.slides = [...this.querySelectorAll("[data-gallery-slide]")];
      this.thumbs = [...this.querySelectorAll("[data-gallery-thumb]")];
      this.thumbList = this.querySelector(".pdp-gallery__thumbs");
      this.controls = this.querySelector("[data-gallery-controls]");
      this.prevBtn = this.querySelector("[data-gallery-prev]");
      this.nextBtn = this.querySelector("[data-gallery-next]");
      this.dialog = this.querySelector("[data-gallery-zoom-dialog]");
      this.zoomTrack = this.dialog?.querySelector("[data-zoom-track]");
      this.zoomSlides = this.dialog
        ? [...this.dialog.querySelectorAll("[data-zoom-slide]")]
        : [];
      this.zoomPrevBtn = this.dialog?.querySelector("[data-zoom-prev]");
      this.zoomNextBtn = this.dialog?.querySelector("[data-zoom-next]");
      this.index = 0;
      this.zoomIndex = 0;

      if (!this.track || this.slides.length === 0) return;

      // The control cluster is server-rendered `hidden` so no-JS users
      // never see dead buttons (native scroll + thumbnail anchors still
      // navigate); reveal the whole instrument now that it works.
      this.controls?.removeAttribute("hidden");

      this.prevBtn?.addEventListener("click", () => this.goTo(this.index - 1));
      this.nextBtn?.addEventListener("click", () => this.goTo(this.index + 1));

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener("click", (event) => {
          event.preventDefault();
          this.goTo(i);
          this.alignBelowHeader();
        });
      });

      let scrollTimer = null;
      this.track.addEventListener(
        "scroll",
        () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => {
            const width = this.track.clientWidth;
            if (!width) return;
            const i = Math.round(this.track.scrollLeft / width);
            this.setCurrent(Math.max(0, Math.min(i, this.slides.length - 1)));
          }, 80);
        },
        { passive: true },
      );

      this.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-zoom-trigger]");
        if (trigger) this.openZoom(trigger);
      });

      if (this.dialog) {
        this.dialog.addEventListener("click", (event) => {
          // Prev/next have their own handlers.
          if (event.target.closest("[data-zoom-prev],[data-zoom-next]")) return;
          // Explicit close button.
          if (event.target.closest("[data-zoom-close]")) {
            this.closeZoom();
            return;
          }
          // Clicking the image or video (its controls) keeps the lightbox
          // open; the backdrop or empty area beside the media closes it.
          if (!event.target.closest(".pdp-zoom__img, .pdp-zoom__video")) {
            this.closeZoom();
          }
        });

        this.zoomPrevBtn?.addEventListener("click", () =>
          this.zoomGoTo(this.zoomIndex - 1),
        );
        this.zoomNextBtn?.addEventListener("click", () =>
          this.zoomGoTo(this.zoomIndex + 1),
        );

        this.dialog.addEventListener("keydown", (event) => {
          // Let native video controls handle arrow keys when focused.
          if (event.target.closest("video")) return;
          if (event.key === "ArrowRight") {
            event.preventDefault();
            this.zoomGoTo(this.zoomIndex + 1);
          } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            this.zoomGoTo(this.zoomIndex - 1);
          }
        });

        // Stop playback when the lightbox is dismissed (close button, Esc or
        // backdrop) so nothing keeps playing out of view.
        this.dialog.addEventListener("close", () => this.pauseZoomVideos());

        if (this.zoomTrack) {
          let zoomScrollTimer = null;
          this.zoomTrack.addEventListener(
            "scroll",
            () => {
              window.clearTimeout(zoomScrollTimer);
              zoomScrollTimer = window.setTimeout(() => {
                const width = this.zoomTrack.clientWidth;
                if (!width) return;
                const i = Math.round(this.zoomTrack.scrollLeft / width);
                this.setZoomCurrent(
                  Math.max(0, Math.min(i, this.zoomSlides.length - 1)),
                );
              }, 80);
            },
            { passive: true },
          );
        }
      }

      // Initial state: sync the controls without starting any video.
      this.setCurrent(0, { playVideo: false });
    }

    goTo(index) {
      if (!this.slides.length) return;
      const i = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({
        left: this.slides[i].offsetLeft,
        behavior: reducedMotion.matches ? "auto" : "smooth",
      });
      this.setCurrent(i);
    }

    setCurrent(index, { playVideo = true } = {}) {
      this.index = index;
      this.thumbs.forEach((thumb, i) => {
        if (i === index) {
          thumb.setAttribute("aria-current", "true");
        } else {
          thumb.removeAttribute("aria-current");
        }
      });
      if (this.prevBtn) this.prevBtn.disabled = index === 0;
      if (this.nextBtn)
        this.nextBtn.disabled = index === this.slides.length - 1;
      // Pause any playing video that scrolled out of view.
      this.slides.forEach((slide, i) => {
        if (i === index) return;
        slide.querySelectorAll("video").forEach((video) => video.pause());
      });
      if (playVideo) this.playSlideVideo(index);
      this.scrollThumbIntoView(index);
    }

    // Autoplay (muted) the video on the slide the shopper navigated to —
    // arrows, thumbnails or swipe. Muted playback satisfies browser
    // autoplay policies; the native controls let them unmute.
    playSlideVideo(index) {
      if (reducedMotion.matches) return;
      const video = this.slides[index]?.querySelector("video");
      if (!video) return;
      // Already playing (e.g. the scroll-settle re-fires on the same
      // slide): leave it alone so a shopper's unmute isn't reverted.
      if (!video.paused) return;
      video.muted = true;
      const played = video.play();
      if (played && typeof played.catch === "function") {
        played.catch(() => {});
      }
    }

    // A thumbnail click changes the visible slide, but when the page is
    // scrolled so the gallery sits behind the sticky header the new image
    // stays cut off. Nudge the page until the gallery clears the header.
    alignBelowHeader() {
      const viewport = this.querySelector(".pdp-gallery__viewport");
      if (!viewport) return;
      const header =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--header-height",
          ),
        ) || 0;
      const offset = viewport.getBoundingClientRect().top - (header + 12);
      if (offset >= -1) return; // already fully visible below the header
      window.scrollBy({
        top: offset,
        behavior: reducedMotion.matches ? "auto" : "smooth",
      });
    }

    // Keep the active thumbnail within view of the (scrollable) thumb row —
    // e.g. on mobile, where only ~4 of many thumbnails are visible at once.
    scrollThumbIntoView(index) {
      const list = this.thumbList;
      const thumb = this.thumbs[index];
      if (!list || !thumb) return;
      if (list.scrollWidth <= list.clientWidth + 1) return; // no overflow
      const listRect = list.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      const pad = 12;
      let delta = 0;
      if (thumbRect.left < listRect.left + pad) {
        delta = thumbRect.left - listRect.left - pad;
      } else if (thumbRect.right > listRect.right - pad) {
        delta = thumbRect.right - listRect.right + pad;
      }
      if (delta === 0) return;
      list.scrollBy({
        left: delta,
        behavior: reducedMotion.matches ? "auto" : "smooth",
      });
    }

    goToMediaId(mediaId) {
      if (!mediaId) return;
      const i = this.slides.findIndex(
        (slide) => slide.getAttribute("data-media-id") === String(mediaId),
      );
      if (i >= 0) this.goTo(i);
    }

    openZoom(trigger) {
      if (!this.dialog || !this.zoomTrack || !this.zoomSlides.length) return;
      // The single zoom control carries no per-slide index, so open the
      // lightbox at whatever slide is currently in view.
      const requested = parseInt(trigger.getAttribute("data-zoom-index"), 10);
      const i = Math.max(
        0,
        Math.min(
          Number.isNaN(requested) ? this.index : requested,
          this.zoomSlides.length - 1,
        ),
      );
      this.ensureZoomLoaded(i);
      // Pause any inline gallery video so it doesn't keep playing (with sound)
      // behind the lightbox.
      this.slides.forEach((slide) => slide.querySelector("video")?.pause());
      this.dialog.showModal();
      // Jump to the requested image *before the first paint*: because each
      // image is its own element (no src swap) and the position is set
      // synchronously, the previously-viewed image never flashes and the
      // track doesn't animate in from index 0.
      this.zoomTrack.scrollLeft = i * this.zoomTrack.clientWidth;
      this.setZoomCurrent(i);
    }

    zoomGoTo(index) {
      if (!this.zoomSlides.length) return;
      const i = Math.max(0, Math.min(index, this.zoomSlides.length - 1));
      this.zoomTrack.scrollTo({
        left: i * this.zoomTrack.clientWidth,
        behavior: reducedMotion.matches ? "auto" : "smooth",
      });
      this.setZoomCurrent(i);
    }

    setZoomCurrent(index) {
      this.zoomIndex = index;
      if (this.zoomPrevBtn) this.zoomPrevBtn.disabled = index === 0;
      if (this.zoomNextBtn)
        this.zoomNextBtn.disabled = index === this.zoomSlides.length - 1;
      this.ensureZoomLoaded(index);
      this.playZoomMedia(index);
    }

    // Eagerly load the current image and its neighbours so swiping stays
    // smooth; the rest stay lazy to keep the initial page light.
    ensureZoomLoaded(index) {
      [index - 1, index, index + 1].forEach((n) => {
        const img = this.zoomSlides[n]?.querySelector("img");
        if (img && img.getAttribute("loading") === "lazy") {
          img.setAttribute("loading", "eager");
        }
      });
    }

    // Autoplay (muted) the video on the active zoom slide and pause the rest,
    // so landing on a video plays it without sound. External embeds keep their
    // own controls and are left untouched.
    playZoomMedia(index) {
      this.zoomSlides.forEach((slide, i) => {
        const video = slide.querySelector("video");
        if (!video) return;
        if (i === index) {
          // Keep a video the shopper already started (and possibly
          // unmuted) untouched, and never autoplay for reduced motion —
          // the pause branch below still runs for the other slides.
          if (!video.paused || reducedMotion.matches) return;
          video.muted = true;
          const played = video.play();
          if (played && typeof played.catch === "function") {
            played.catch(() => {});
          }
        } else {
          video.pause();
        }
      });
    }

    pauseZoomVideos() {
      this.zoomSlides.forEach((slide) => {
        slide.querySelector("video")?.pause();
        // External embeds (YouTube/Vimeo) expose no pause API without
        // enablejsapi; reloading the iframe is the reliable way to stop
        // audio from continuing behind the closed dialog.
        const frame = slide.querySelector("iframe");
        if (frame) frame.src = frame.src; // eslint-disable-line no-self-assign
      });
    }

    closeZoom() {
      // Stop playback first so it can't linger if the `close` event is missed.
      this.pauseZoomVideos();
      this.dialog.close();
    }
  }

  /* ------------------------------------------------------------
     Quantity selector.
     ------------------------------------------------------------ */
  class PdpQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="quantity"]');
      if (!this.input) return;

      this.addEventListener("click", (event) => {
        const btn = event.target.closest("[data-qty-change]");
        if (!btn) return;
        event.preventDefault();
        const step = parseInt(btn.getAttribute("data-qty-change"), 10) || 0;
        const min = parseInt(this.input.min, 10) || 1;
        let value = parseInt(this.input.value, 10);
        if (Number.isNaN(value)) value = min;
        this.input.value = Math.max(min, value + step);
        this.input.dispatchEvent(new Event("change", { bubbles: true }));
      });

      this.input.addEventListener("change", () => {
        const min = parseInt(this.input.min, 10) || 1;
        const value = parseInt(this.input.value, 10);
        if (Number.isNaN(value) || value < min) this.input.value = min;
      });
    }
  }

  /* ------------------------------------------------------------
     Variant picker.
     Resolves the variant from the option radios, keeps the
     no-JS fallback <select name="id"> in sync, updates the URL,
     jumps the gallery to the variant media and re-renders the
     price/button regions through the Section Rendering API so
     money stays formatted server-side (markets & currencies).
     ------------------------------------------------------------ */
  class PdpVariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute("data-section");
      this.productUrl = this.getAttribute("data-url");
      this.select = this.querySelector('select[name="id"]');
      this.abortController = null;

      try {
        this.variants = JSON.parse(
          this.querySelector("[data-variant-json]")?.textContent || "[]",
        );
      } catch {
        this.variants = [];
      }

      this.classList.add("js");

      this.addEventListener("change", (event) => {
        if (event.target.matches('input[type="radio"]')) this.onOptionChange();
      });
    }

    selectedOptions() {
      return [...this.querySelectorAll("fieldset[data-option-index]")].map(
        (fieldset) =>
          fieldset.querySelector('input[type="radio"]:checked')?.value ?? null,
      );
    }

    onOptionChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, i) => option === options[i]),
      );
      this.updateVariant(variant || null);
    }

    updateVariant(variant) {
      if (!variant) {
        // The chosen option combination has no variant: disable the
        // buy buttons instead of pretending a default variant.
        const scope = document.querySelector(
          `[data-pdp-section="${this.sectionId}"]`,
        );
        const text = this.getAttribute("data-unavailable-text") || "";
        scope
          ?.querySelectorAll(
            '[data-swap="atc-label"], [data-swap="sticky-atc-label"]',
          )
          .forEach((label) => {
            label.textContent = text;
            const button = label.closest("button");
            if (button) button.disabled = true;
          });
        return;
      }
      if (this.select) this.select.value = variant.id;

      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url.toString());

      const gallery = document.querySelector(
        `pdp-gallery[data-section="${this.sectionId}"]`,
      );
      if (variant.featured_media)
        gallery?.goToMediaId(variant.featured_media.id);

      this.renderSection(variant.id);

      this.dispatchEvent(
        new CustomEvent("pdp:variant:change", {
          bubbles: true,
          detail: { variant, sectionId: this.sectionId },
        }),
      );
    }

    renderSection(variantId) {
      if (!this.sectionId || !this.productUrl) return;
      this.abortController?.abort();
      this.abortController = new AbortController();

      const url =
        `${this.productUrl}?section_id=${encodeURIComponent(this.sectionId)}` +
        (variantId ? `&variant=${encodeURIComponent(variantId)}` : "");

      fetch(url, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const scope = document.querySelector(
            `[data-pdp-section="${this.sectionId}"]`,
          );
          if (!scope) return;
          scope.querySelectorAll("[data-swap]").forEach((el) => {
            const next = doc.querySelector(
              `[data-swap="${el.getAttribute("data-swap")}"]`,
            );
            if (!next) return;
            el.innerHTML = next.innerHTML;
            if (next.hasAttribute("data-disabled")) {
              const button = el.closest("button");
              if (button)
                button.disabled = next.getAttribute("data-disabled") === "true";
            }
          });
        })
        .catch((error) => {
          if (error?.name !== "AbortError") {
            console.warn("[pdp] section render failed", error); // eslint-disable-line no-console
          }
        });
    }
  }

  /* ------------------------------------------------------------
     Product form: ajax add-to-cart wired to the theme's drawer
     cart (theme.Drawer). Falls back to a normal POST navigation
     when JS is unavailable, and to the cart page when no drawer
     is present.
     ------------------------------------------------------------ */
  class PdpProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      this.error = this.querySelector("[data-form-error]");
      this.form?.addEventListener("submit", (event) => this.onSubmit(event));
    }

    submitButtons() {
      const inside = [...this.form.querySelectorAll('button[type="submit"]')];
      // getAttribute, not .id: the form always carries an <input name="id">
      // for the variant, and HTMLFormElement's named-property getter shadows
      // the id property with that element. `form.id` returns the input, so
      // the selector became button[form="[object HTMLInputElement]"] and no
      // cross-section button ever got the loading state this method exists for.
      const formId = this.form.getAttribute("id");
      const outside = formId
        ? [...document.querySelectorAll(`button[form="${formId}"]`)]
        : [];
      return inside.concat(outside);
    }

    setLoading(isLoading) {
      this.submitButtons().forEach((button) => {
        if (isLoading) {
          button.dataset.wasDisabled = String(button.disabled);
          button.setAttribute("aria-busy", "true");
          button.disabled = true;
        } else {
          button.removeAttribute("aria-busy");
          // Restore the pre-request state (e.g. sold out stays disabled).
          button.disabled = button.dataset.wasDisabled === "true";
          delete button.dataset.wasDisabled;
        }
      });
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || "";
    }

    onSubmit(event) {
      event.preventDefault();

      this.showError("");
      this.setLoading(true);

      const formData = new FormData(this.form);
      const drawer = document.querySelector("[data-cart-drawer]");
      const drawerSectionId = drawer?.getAttribute("data-section-id");
      if (drawerSectionId) {
        formData.append("sections", drawerSectionId);
        formData.append(
          "sections_url",
          window.location.pathname + window.location.search,
        );
      }

      const action = this.form.getAttribute("action") || "/cart/add";

      fetch(`${action}.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      })
        .then((response) =>
          response.json().then((data) => {
            if (!response.ok)
              throw Object.assign(new Error("Add to cart failed"), { data });
            return data;
          }),
        )
        .then((item) => {
          document.dispatchEvent(
            new CustomEvent("pdp:cart:added", {
              bubbles: true,
              detail: { item },
            }),
          );
          if (window.theme?.Drawer?.addToCartOkay) {
            window.theme.Drawer.addToCartOkay(item);
          } else {
            // No drawer on this page — take the shopper to the cart.
            window.location.href =
              this.getAttribute("data-cart-url") || "/cart";
          }
        })
        .catch((error) => {
          this.showError(
            error?.data?.description ||
              error?.data?.message ||
              this.getAttribute("data-error-message") ||
              "",
          );
        })
        .finally(() => this.setLoading(false));
    }
  }

  /* ------------------------------------------------------------
     Sticky add-to-bag bar: appears once the buy box scrolls out
     of view and steps aside again when the footer comes in.
     While the bar is up, the Gorgias chat launcher is hidden so
     the two never overlap (the body class is also honored by
     handleChatWidget in intercom-custom-script.js, whose scroll
     handler would otherwise re-show the launcher).
     Stays hidden entirely without JS.
     ------------------------------------------------------------ */
  class PdpStickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector(
        this.getAttribute("data-watch") || "",
      );
      if (!target || !("IntersectionObserver" in window)) return;

      this.pastBuyBox = false;
      this.footerInView = false;

      // Read the NEWEST record: the browser batches records when the main
      // thread is busy, and acting on an old one would latch stale state.
      this.buyBoxObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          this.pastBuyBox =
            !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          this.update();
        },
        { threshold: 0 },
      );
      this.buyBoxObserver.observe(target);

      // Explicit priority (a selector list would match in document order,
      // so a stray <footer> earlier in the page could win otherwise).
      const footer =
        document.querySelector("#shopify-section-footer") ||
        document.querySelector('[data-section-type="footer"]') ||
        document.querySelector("footer");
      if (footer) {
        this.footerObserver = new IntersectionObserver(
          (entries) => {
            this.footerInView = entries[entries.length - 1].isIntersecting;
            this.update();
          },
          { threshold: 0 },
        );
        this.footerObserver.observe(footer);
      }
    }

    disconnectedCallback() {
      this.buyBoxObserver?.disconnect();
      this.footerObserver?.disconnect();
      // Reset to the detached state so a re-attach (theme editor moves the
      // node) passes update()'s change guard, and don't leave the chat
      // launcher suppressed meanwhile.
      this.hidden = true;
      document.body.classList.remove("pdp-sticky-atc-visible");
      this.syncChat();
    }

    update() {
      const visible = this.pastBuyBox && !this.footerInView;
      if (visible === !this.hidden) return;
      this.hidden = !visible;
      document.body.classList.toggle("pdp-sticky-atc-visible", visible);
      this.syncChat();
    }

    // Chat launcher visibility goes through the theme's central arbiter
    // (handleChatWidget also honors an open chat window and the 500px
    // scroll rule); fall back to a direct toggle when it isn't loaded.
    syncChat() {
      if (typeof window.handleChatWidget === "function") {
        window.handleChatWidget();
      } else if (window.theme?.ChatWidget) {
        if (this.hidden) {
          window.theme.ChatWidget.show();
        } else {
          window.theme.ChatWidget.hide();
        }
      }
    }
  }

  /* ------------------------------------------------------------
     FAQ: the "show all questions" disclosure.
     Every answer is server-rendered; the rows past the fold are
     only hidden with CSS, so search and AI surfaces read the whole
     list and neither state shifts the layout. Without JS a
     <noscript> style in the section reveals those rows and drops
     this toggle, so no answer is ever unreachable.
     ------------------------------------------------------------ */
  class PdpFaq extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector("[data-faq-toggle]");
      if (!this.toggle) return;

      this.onToggle = () => this.setExpanded(!this.expanded);
      this.toggle.addEventListener("click", this.onToggle);
    }

    disconnectedCallback() {
      if (this.toggle && this.onToggle) {
        this.toggle.removeEventListener("click", this.onToggle);
      }
    }

    get expanded() {
      return this.classList.contains("is-expanded");
    }

    setExpanded(expanded) {
      const overflow = this.querySelector(".pdp-faq__overflow");

      // Collapsing is about to hide whatever holds focus, so reclaim it
      // on the control the user is standing on before the rows go away.
      if (!expanded && overflow?.contains(document.activeElement)) {
        this.toggle.focus();
      }

      this.classList.toggle("is-expanded", expanded);
      this.toggle.setAttribute("aria-expanded", String(expanded));

      // A row left open would spring back open on the next expand.
      if (!expanded && overflow) {
        overflow.querySelectorAll(".pdp-accordion[open]").forEach((row) => {
          row.open = false;
        });
      }
    }
  }

  /* ------------------------------------------------------------
     Compare: optional recommendation refinement.
     The table itself is server-rendered and already on the page — this
     element only runs when the section's `enable_recommendations` setting
     is on, because the `recommendations` Liquid object cannot be populated
     until a section is fetched through the Product Recommendations API.
     It re-fetches this same section, which re-runs the same selection with
     recommendations in hand, and swaps the result in.

     It never removes or empties the host: whatever is already rendered is
     real, complete content, so a failed fetch, a timeout, or a response
     holding no table all leave the server-rendered table exactly as it is.
     ------------------------------------------------------------ */
  class PdpCompare extends HTMLElement {
    connectedCallback() {
      this.url = this.dataset.compareUrl;

      // No URL means recommendations are switched off, or this element came
      // back from the fetch below. Either way the server-rendered table
      // stands on its own.
      if (!this.url) return;

      // Purely a refinement, so it waits until the shopper is heading toward
      // the band: off the critical path, and skipped entirely on a bounce.
      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: "600px 0px" }
        );
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
    }

    async load() {
      const url = this.url;
      this.url = null; // one attempt — a retry loop would hammer the endpoint

      this.controller = new AbortController();
      const timer = window.setTimeout(() => this.controller.abort(), 10000);

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) {
          throw new Error("Compare section responded " + response.status);
        }

        const doc = new DOMParser().parseFromString(
          await response.text(),
          "text/html"
        );
        const host = doc.querySelector("pdp-compare");
        const content =
          host?.querySelector("[data-compare-content]") ||
          doc.querySelector("[data-compare-content]");
        const target = this.querySelector("[data-compare-content]");

        // Nothing better came back. Keep what the server rendered.
        if (!content || !content.querySelector(".pdp-compare__table")) return;
        if (!target) return;

        // The rendered host carries the column count as a custom property.
        const style = host?.getAttribute("style");
        if (style) this.setAttribute("style", style);

        target.replaceWith(document.importNode(content, true));
        this.classList.add("is-loaded");
      } catch (error) {
        // The server-rendered table is still on the page and still correct,
        // so a failed or aborted refinement is a no-op, not a teardown.
        if (error.name !== "AbortError") {
          console.warn("[pdp-compare]", error);
        }
      } finally {
        window.clearTimeout(timer);
      }
    }
  }

  if (!customElements.get("pdp-gallery"))
    customElements.define("pdp-gallery", PdpGallery);
  if (!customElements.get("pdp-quantity"))
    customElements.define("pdp-quantity", PdpQuantity);
  if (!customElements.get("pdp-variant-picker")) {
    customElements.define("pdp-variant-picker", PdpVariantPicker);
  }
  if (!customElements.get("pdp-product-form")) {
    customElements.define("pdp-product-form", PdpProductForm);
  }
  if (!customElements.get("pdp-sticky-atc")) {
    customElements.define("pdp-sticky-atc", PdpStickyAtc);
  }
  if (!customElements.get("pdp-faq")) {
    customElements.define("pdp-faq", PdpFaq);
  }
  if (!customElements.get("pdp-compare")) {
    customElements.define("pdp-compare", PdpCompare);
  }
})();
